/** Post-delivery validation — gateway /pfm/delivery-validation + UI banner. */

export type PfmDeliveryOutcomeCode =
  | "none"
  | "pending_import"
  | "no_changes"
  | "rejected"
  | "accepted"
  | "accepted_with_warnings";

export type PfmDeliveryValidationPayload = {
  ok?: boolean;
  executionId?: string;
  outcome?: {
    code?: PfmDeliveryOutcomeCode | string;
    message?: string;
    rejectionCode?: string;
    nextDirection?: string;
    pfmRevision?: number;
    fixRequired?: boolean;
    warningKind?: string;
    qualityGateCode?: string;
    ts?: number;
  };
  diskVsDb?: {
    hasCommittedSnapshot?: boolean;
    wouldRebuildFromDelivery?: boolean;
    pfmRevision?: number;
  };
  latestQualityGate?: {
    ts?: number;
    report?: {
      accepted?: boolean;
      code?: string;
      message?: string;
      aiJudge?: {
        nextCycleFeedback?: string;
        summary?: string;
        verdict?: string;
        confidence?: number;
      };
    };
  };
  executorHint?: string;
  pfmRevision?: number;
};

export type PfmDeliveryValidationBanner = {
  severity: "info" | "warning" | "error" | "success";
  titleKey: string;
  titleFallback: string;
  message: string;
  detail?: string;
  show: boolean;
};

function pickStr(...values: unknown[]): string {
  for (const v of values) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return "";
}

function pickNum(...values: unknown[]): number {
  for (const v of values) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

export function buildPfmDeliveryValidationBanner(
  payload: PfmDeliveryValidationPayload | null | undefined,
): PfmDeliveryValidationBanner | null {
  if (!payload?.ok) return null;
  const outcome = payload.outcome || {};
  const code = String(outcome.code || "none").trim().toLowerCase() as PfmDeliveryOutcomeCode;
  if (code === "none") return null;

  const rev = pickNum(outcome.pfmRevision, payload.pfmRevision, payload.diskVsDb?.pfmRevision);
  const revLabel = rev > 0 ? `Rev ${rev}` : "";
  const rejectionCode = pickStr(outcome.rejectionCode, outcome.qualityGateCode);
  const message = pickStr(outcome.message);
  const nextDirection = pickStr(
    outcome.nextDirection,
    payload.latestQualityGate?.report?.aiJudge?.nextCycleFeedback,
  );
  const qgSummary = pickStr(payload.latestQualityGate?.report?.aiJudge?.summary);

  if (code === "rejected") {
    return {
      severity: "error",
      titleKey: "runs.deliveryValidation.rejectedTitle",
      titleFallback: "PFM delivery rejected",
      message: message || pickStr(payload.executorHint) || "Mind map did not pass validation.",
      detail: [rejectionCode ? `Code: ${rejectionCode}` : "", nextDirection || qgSummary]
        .filter(Boolean)
        .join(" · "),
      show: true,
    };
  }

  if (code === "no_changes") {
    return {
      severity: "info",
      titleKey: "runs.deliveryValidation.noChangesTitle",
      titleFallback: "No delivery change",
      message:
        message ||
        (revLabel
          ? `${revLabel} unchanged — .pfm on disk matches the last DB commit.`
          : "No changes detected since the last DB commit."),
      detail: pickStr(outcome.nextDirection) || undefined,
      show: true,
    };
  }

  if (code === "pending_import") {
    return {
      severity: "warning",
      titleKey: "runs.deliveryValidation.pendingTitle",
      titleFallback: "Delivery pending import",
      message:
        message ||
        "New .pfm/.FMR files on disk — waiting for import_pfm_delivery to update the mindmap.",
      show: true,
    };
  }

  if (code === "accepted_with_warnings") {
    const warningKind = String(outcome.warningKind || "").trim();
    const titleFallback =
      warningKind === "flat_treelist"
        ? "Delivery saved — flat mindmap"
        : "Delivery saved — crowded levels";
    return {
      severity: "warning",
      titleKey: "runs.deliveryValidation.warningTitle",
      titleFallback,
      message: message || "Mindmap saved with structure warnings.",
      detail: nextDirection || undefined,
      show: true,
    };
  }

  if (code === "accepted") {
    return {
      severity: "success",
      titleKey: "runs.deliveryValidation.acceptedTitle",
      titleFallback: revLabel ? `PFM delivery accepted (${revLabel})` : "PFM delivery accepted",
      message: message || "Mind map and node reports synced to DB.",
      detail: nextDirection || qgSummary || undefined,
      show: true,
    };
  }

  return null;
}

export function shouldPollPfmDeliveryValidation(opts: {
  isLiveRun: boolean;
  loginSuccessful: boolean;
  projectRunStatus: number;
}): boolean {
  if (!opts.loginSuccessful) return false;
  if (opts.isLiveRun) return opts.projectRunStatus >= 2;
  return false;
}
